import * as React from "react";
import {useParams} from "react-router-dom";

import {FetchPolicy} from "@khanacademy/wonder-blocks-data";
import {useQuery} from "@khan/data-gql";
import {PermissionsInfo, useUserAttributes} from "@khan/shared-core";

import {CLASSROOM_BY_CODE_QUERY} from "#content-library/queries.ts";
import {
    getPolarisRedirectUrl,
    shouldRedirectToKaClassroom,
} from "#shared-package/polaris-redirect-util.ts";
import FullPageRedirect from "#shared/app-shell/full-page-redirect.dev.tsx";

import AsyncRouteHandler from "./async-route-handler.tsx";
import LoginRequiredRoute from "./login-required-route.tsx";
import RouteLoadSpinner from "./route-load-spinner.tsx";
import type {CommonRouteProps} from "./types.ts";

// Import types for login-required props
/**
 * Login requirements to gate access to a route.
 *
 * - phantomUserAllowed: Whether an anonymous (phantom) user may access the route.
 *   Defaults to: false
 * - childUserAllowed: Whether a child account may access the route.
 *   Defaults to: true
 * - mobileAppUserAllowed: Whether a user authenticated via the mobile app may access the route.
 *   Defaults to: false
 */
type LoginRequiredDefaultProps = {
    /** Whether an anonymous (phantom) user may access the route. */
    phantomUserAllowed?: boolean;
    /** Whether a child account may access the route. */
    childUserAllowed?: boolean;
    /** Whether a user authenticated via the mobile app may access the route. */
    mobileAppUserAllowed?: boolean;
};

type LoginRequiredProps = LoginRequiredDefaultProps & CommonRouteProps;

/**
 * Props for the classroom redirect handler wrapper.
 *
 * - component: The lazily-loaded component to render when not redirecting.
 * - template: Optional route template to pass through to the async handler.
 */
type ClassroomRedirectHandlerProps = {
    /** The route component to render when not redirecting. */
    component: CommonRouteProps["component"];
    /** Optional route template for the async handler. */
    template?: CommonRouteProps["template"];
};

/**
 * Component that checks if a classroom should redirect to KAC (Khan Academy Classroom).
 * If redirect is needed, renders a redirect. Otherwise, renders the wrapped component.
 * Follows the same pattern as AsyncRouteHandler for wrapping components.
 */
function ClassroomRedirectHandler({
    component,
    template,
}: ClassroomRedirectHandlerProps): React.ReactElement {
    const params = useParams<{classCode: string}>();
    const classCode = params.classCode;
    const userAttributes = useUserAttributes();

    const [classroomResult] = useQuery(CLASSROOM_BY_CODE_QUERY, {
        variables: {classCode: classCode || ""},
        fetchPolicy: FetchPolicy.CacheAndNetwork,
        // Only run the query if we have a classCode AND user is logged in
        skip: !classCode || !PermissionsInfo.isLoggedIn,
    });

    // Show loading spinner while checking for redirect
    if (classroomResult.status === "loading") {
        return <RouteLoadSpinner />;
    }

    // If this is a Khan Classroom and we're not embedded, redirect to Polaris
    if (
        classroomResult.status === "success" &&
        classroomResult.data?.studentList &&
        shouldRedirectToKaClassroom(
            classroomResult.data.studentList.isKhanClassroom,
        )
    ) {
        const redirectUrl = getPolarisRedirectUrl(
            classroomResult.data.studentList.signupCode ?? undefined,
            userAttributes,
        );
        return <FullPageRedirect to={redirectUrl} />;
    }

    // All non-success query states fall through to rendering the route normally
    // (loading is handled above). If not a Khan Classroom, render normally too.
    return <AsyncRouteHandler component={component} template={template} />;
}

/**
 * ClassroomRedirectRoute: Handle routing to a classroom page that may need
 * to redirect to Polaris (Khan Academy Classroom) based on classroom data.
 *
 * This wraps the provided route with login requirements and, once the
 * classroom query resolves, performs a redirect if the classroom is a
 * Khan Classroom. Otherwise, it renders the route normally.
 */
export default function ClassroomRedirectRoute(
    props: LoginRequiredProps,
): React.ReactElement {
    const {
        exact,
        path,
        component,
        template,
        phantomUserAllowed = false,
        childUserAllowed = true,
        mobileAppUserAllowed = false,
    } = props;

    return (
        <LoginRequiredRoute
            exact={exact}
            path={path}
            phantomUserAllowed={phantomUserAllowed}
            childUserAllowed={childUserAllowed}
            mobileAppUserAllowed={mobileAppUserAllowed}
            component={() => (
                <ClassroomRedirectHandler
                    component={component}
                    template={template}
                />
            )}
        />
    );
}
